import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ScanSearch, Search, Tag, Warehouse, Box, Truck, FileClock, CircleDot, Package, X,
} from "lucide-react";

const ICONS = {
  tag: Tag, warehouse: Warehouse, box: Box, truck: Truck, audit: FileClock, status: CircleDot, dot: CircleDot, flow: Package,
};

export default function Trace() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const run = useCallback(async (q) => {
    if (!q) return;
    setBusy(true); setNotFound(false); setData(null);
    try {
      const { data: res } = await api.get("/trace", { params: { code: q } });
      setData(res);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  }, [api]);

  useEffect(() => {
    const q = params.get("code");
    if (q) { setCode(q); run(q); }
  }, [params, run]);

  const search = async (e) => {
    e?.preventDefault?.();
    run(code.trim());
  };

  const label = data?.label;
  const ship = data?.shipment;

  return (
    <div className="space-y-4" data-testid="trace-page">
      <div>
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <ScanSearch className="w-5 h-5 text-red-600" /> {t("trace")}
        </h2>
        <p className="text-sm text-slate-500">{t("trace_timeline")}</p>
      </div>

      <form onSubmit={search} className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
        <input
          data-testid="trace-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t("trace_search")}
          className="w-full ps-9 pe-24 py-3 border border-slate-300 rounded-xl text-sm outline-none focus:border-red-600 bg-white font-mono"
        />
        <button type="submit" data-testid="trace-submit" disabled={busy} className="absolute top-1/2 -translate-y-1/2 end-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-1.5 rounded-lg">
          {busy ? "…" : t("search")}
        </button>
      </form>

      {notFound && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400" data-testid="trace-no-result">
          <Package className="w-14 h-14 mx-auto opacity-30 mb-3" />
          <p>{t("trace_no_result")}</p>
        </div>
      )}

      {data && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Summary */}
          <div className="space-y-3">
            {label && (
              <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="trace-label-card">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-bold uppercase text-indigo-600">{t("label")}</span>
                </div>
                <div className="font-mono font-bold text-sm">{label.codigo}</div>
                <dl className="mt-2 text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between"><dt className="text-slate-400">{t("units")}</dt><dd>{label.numero_paquetes}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">{t("weight_kg")}</dt><dd>{label.peso_recibido || label.peso_total} kg</dd></div>
                  {label.cliente_numero_cuenta && <div className="flex justify-between"><dt className="text-slate-400">{t("account_number")}</dt><dd className="font-mono text-indigo-600">{label.cliente_numero_cuenta}</dd></div>}
                  {label.estado_almacen && <div className="flex justify-between"><dt className="text-slate-400">{t("warehouse")}</dt><dd className="text-emerald-600">{label.estado_almacen}</dd></div>}
                </dl>
              </div>
            )}
            {ship && (
              <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="trace-ship-card">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-bold uppercase text-emerald-600">{t("shipment")}</span>
                </div>
                <div className="font-mono font-bold text-sm">{ship.tracking_code}</div>
                <dl className="mt-2 text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between"><dt className="text-slate-400">{t("status")}</dt><dd>{t(`status_${ship.estado}`) || ship.estado}</dd></div>
                  {ship.destino && <div className="flex justify-between"><dt className="text-slate-400">{t("destination")}</dt><dd>{ship.destino}</dd></div>}
                  <div className="flex justify-between"><dt className="text-slate-400">{t("weight_kg")}</dt><dd>{ship.peso} kg</dd></div>
                </dl>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-xs font-bold uppercase text-slate-500 mb-4">{t("trace_timeline")} · {data.events.length}</div>
            <ol className="relative border-s-2 border-slate-100 ms-3 space-y-5">
              {data.events.map((ev, i) => {
                const Icon = ICONS[ev.icon] || CircleDot;
                return (
                  <li key={`${ev.fecha}-${ev.fase_key || ev.fase}-${i}`} className="ms-6" data-testid={`trace-event-${i}`}>
                    <span className="absolute -start-[13px] w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center">
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-900">{ev.fase_key ? (t(`trace_${ev.fase_key}`) !== `trace_${ev.fase_key}` ? t(`trace_${ev.fase_key}`) : ev.fase) : ev.fase}</span>
                      <span className="text-[11px] text-slate-400 tabular-nums">{new Date(ev.fecha).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {ev.actor && ev.actor !== "—" && <span className="font-medium text-slate-700">{ev.actor}</span>}
                      {ev.oficina && <span className="ms-1">· {ev.oficina}</span>}
                      {ev.detalle && <span className="block text-slate-400">{ev.detalle}</span>}
                    </div>
                  </li>
                );
              })}
              {data.events.length === 0 && <li className="ms-6 text-sm text-slate-400">—</li>}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
